/**
 * Turning a user-agent string into something a person can read.
 *
 * The account page listed sessions as
 * `Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like…`
 * — twenty-eight rows of it, truncated mid-token, with no way to tell one
 * device from another. The point of a session list is recognising a sign-in
 * you did not make, and a wall of identical strings makes that impossible.
 *
 * Deliberately crude. Proper UA parsing needs a library and a data file that
 * goes stale, and gets no better at the only question being asked here: is
 * this me, on a device I recognise? Browser, platform and a note when it is
 * plainly not a browser is enough to answer that. The raw string stays
 * available on hover for the cases where it is not.
 */

export interface Agent {
  /** "Chrome on Windows", "curl", "Safari on iPhone". */
  label: string
  /** Rough device class, for the icon. */
  kind: 'desktop' | 'mobile' | 'tool'
}

const BROWSERS: Array<[RegExp, string]> = [
  // Order matters: Edge and Opera both claim to be Chrome, and Chrome claims
  // to be Safari. The most specific brand has to be tested first.
  [/\bEdg[ei]?\/[\d.]+/i, 'Edge'],
  [/\bOPR\/|\bOpera\b/i, 'Opera'],
  [/\bSamsungBrowser\//i, 'Samsung Internet'],
  [/\bFirefox\/|\bFxiOS\//i, 'Firefox'],
  [/\bChrome\/|\bCriOS\//i, 'Chrome'],
  [/\bSafari\//i, 'Safari'],
]

const PLATFORMS: Array<[RegExp, string, Agent['kind']]> = [
  [/\biPhone\b/i, 'iPhone', 'mobile'],
  [/\biPad\b/i, 'iPad', 'mobile'],
  [/\bAndroid\b/i, 'Android', 'mobile'],
  [/\bWindows NT\b/i, 'Windows', 'desktop'],
  [/\bMac OS X\b|\bMacintosh\b/i, 'macOS', 'desktop'],
  [/\bCrOS\b/i, 'ChromeOS', 'desktop'],
  [/\bLinux\b/i, 'Linux', 'desktop'],
]

/** Clients that are plainly not someone's browser. */
const TOOLS: Array<[RegExp, string]> = [
  [/^curl\//i, 'curl'],
  [/^Wget\//i, 'Wget'],
  [/^PostmanRuntime\//i, 'Postman'],
  [/^python-requests\//i, 'Python requests'],
  [/^Werkzeug\//i, 'Werkzeug (test client)'],
  [/\bHeadlessChrome\//i, 'Headless Chrome'],
  [/\binsomnia\b/i, 'Insomnia'],
  [/\bokhttp\b/i, 'OkHttp'],
]

export function describeAgent(raw: string | null | undefined): Agent {
  const agent = (raw ?? '').trim()
  if (!agent) return { label: 'Unknown client', kind: 'tool' }

  for (const [pattern, name] of TOOLS) {
    if (pattern.test(agent)) return { label: name, kind: 'tool' }
  }

  const browser = BROWSERS.find(([pattern]) => pattern.test(agent))?.[1]
  const platform = PLATFORMS.find(([pattern]) => pattern.test(agent))

  if (browser && platform) {
    return { label: `${browser} on ${platform[1]}`, kind: platform[2] }
  }
  if (browser) return { label: browser, kind: 'desktop' }
  if (platform) return { label: platform[1], kind: platform[2] }

  // Something we do not recognise. Show the first token rather than nothing —
  // "SomeBot/2.1" is more use than "Unknown".
  return { label: agent.split(/[\s/]/)[0].slice(0, 32) || 'Unknown client', kind: 'tool' }
}

/**
 * Whether this row is most likely the browser reading the page.
 *
 * A guess, and labelled as one in the interface. The API returns no session
 * id, so there is nothing to match on but the user agent and recency — and two
 * tabs of the same browser are indistinguishable. It is worth doing anyway:
 * "don't revoke the one you're using" is the question a person actually has.
 */
export function looksLikeThisDevice(raw: string | null | undefined): boolean {
  if (!raw || typeof navigator === 'undefined') return false
  return raw.trim() === navigator.userAgent.trim()
}
